

(() => {
    'use strict';

    const STORAGE_KEY = 'WebAlias';
    const URL_PARAM = 'als';
    const GOAFFPRO_STORAGE_KEY = 'ref';
    const GOAFFPRO_DCODE_STORAGE_KEY = 'dcode';
    const GOAFFPRO_DISCOUNT_CODE_STORAGE_KEY = 'discount_code';

    const CUSTOMER_TYPE = {
        GUEST: 'guest',
        RETAIL: 'retail',
        AFFILIATE: 'affiliate'
    };

    const DEFAULT_ENDPOINTS = {
        validate: '/webalias/sca/validate',
        retail: '/webalias/sca/retail',
        affiliate: '/webalias/sca/affiliate'
    };

    const normalizeAlias = value => {
        if (typeof value !== 'string') return null;
        const alias = value.trim();
        return alias || null;
    };

    const normalizeTags = tags => Array.isArray(tags) ? tags.map(tag => String(tag).trim().toLowerCase()) : [];
// STORAGE
    class WebAliasStorage {
        get() {
            return normalizeAlias(localStorage.getItem(STORAGE_KEY));
        }

        set(alias, discountCode = null) {
            const value = normalizeAlias(alias);
            if (!value) return;

            localStorage.setItem(STORAGE_KEY, value);
            localStorage.setItem(GOAFFPRO_STORAGE_KEY, value);

            this.setDiscountCode(discountCode);
        }

        setDiscountCode(discountCode) {
            const value = normalizeAlias(discountCode);

            if (!value) {
                localStorage.removeItem(GOAFFPRO_DISCOUNT_CODE_STORAGE_KEY);
                return;
            }

            localStorage.setItem(GOAFFPRO_DISCOUNT_CODE_STORAGE_KEY, value);
        }

        clear() {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(GOAFFPRO_STORAGE_KEY);
            localStorage.removeItem(GOAFFPRO_DISCOUNT_CODE_STORAGE_KEY);
        }
    }
// API
    class WebAliasApi {
        constructor(config) {
            this.baseUrl = String(config.apiBaseUrl || '').replace(/\/$/, '');
            this.endpoints = {...DEFAULT_ENDPOINTS, ...(config.endpoints || {})};
        }

        async validateAlias(alias, signal) {
            const data = await this.post(this.endpoints.validate, {alias}, signal);
            const payload = data?.data ?? data;

            return {
                valid: Boolean(payload?.valid),
                alias: normalizeAlias(payload?.alias ?? payload?.ref_code ?? payload?.refCode ?? payload?.webAlias),
                affiliateId: payload?.affiliateId ?? payload?.affiliate_id ?? payload?.id ?? null,
                discountCode: normalizeAlias(payload?.discountCode)
            };
        }

        async resolveRetailCustomer(customer, signal) {
            const data = await this.post(this.endpoints.retail, {email: customer.email || ''}, signal);
            const payload = data?.data ?? data;

            return {
                locked: Boolean(payload?.locked),
                alias: normalizeAlias(payload?.alias ?? payload?.ref_code ?? payload?.refCode ?? payload?.webAlias),
                source: payload?.source || null
            };
        }

        async resolveAffiliate(customer, signal) {
            const data = await this.post(this.endpoints.affiliate, {email: customer.email || ''}, signal);
            const payload = data?.data ?? data;

            return {
                alias: normalizeAlias(payload?.alias ?? payload?.ref_code ?? payload?.refCode ?? payload?.webAlias)
            };
        }

        async post(endpoint, body, signal) {
            const url = new URL(`${this.baseUrl}${endpoint}`, window.location.origin);

            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body),
                signal
            });

            if (!response.ok) {
                throw new Error(`Web Alias request failed (${response.status})`);
            }

            return response.json();
        }
    }
// RESOLVER
    class BaseWebAliasResolver {
        constructor(api, corporateAlias) {
            this.api = api;
            this.corporateAlias = corporateAlias;
        }

        corporate(source = 'corporate') {
            return {
                alias: this.corporateAlias,
                locked: false,
                source
            };
        }

        async validateCandidate(alias, signal, source) {
            const candidate = normalizeAlias(alias);
            if (!candidate) return null;

            const result = await this.api.validateAlias(candidate, signal);
            if (!result.valid || !result.alias) return null;

            return {
                alias: result.alias,
                discountCode: result.discountCode,
                locked: false,
                source
            };
        }
    }
//FORCE DISCOUNt CODE
    class WebAliasCheckout {
        getDiscountCode() {
            return normalizeAlias(localStorage.getItem(GOAFFPRO_DISCOUNT_CODE_STORAGE_KEY));
        }

        getCheckoutUrl() {
            const discountCode = this.getDiscountCode();
            if (!discountCode) return '/checkout';

            return `/discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent('/checkout')}`;
        }

        install() {
            document.addEventListener('click', this.handleClick.bind(this), true);
        }

        findElement(event, selectors) {
            return event.composedPath().find(node =>
                node instanceof Element && selectors.some(selector => node.matches?.(selector))
            ) || null;
        }

        async handleClick(event) {
            const buyNowButton = this.findElement(event, [
                '.shopify-payment-button__button',
                'shopify-buy-it-now-button'
            ]);

            if (buyNowButton) {
                const discountCode = this.getDiscountCode();
                if (!discountCode) return;

                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();

                console.log('[WebAlias] Buy Now discount:', discountCode);

                await this.buyNowWithDiscount(buyNowButton);
                return;
            }

            const checkoutButton = this.findElement(event, [
                '[name="checkout"]',
                'button[name="checkout"]',
                'a[href="/checkout"]',
                'a[href*="/checkout"]',
                '[data-checkout-button]',
                '[data-cart-checkout]'
            ]);

            if (!checkoutButton) return;

            const discountCode = this.getDiscountCode();
            if (!discountCode) return;

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            console.log('[WebAlias] Checkout discount:', discountCode);
            console.log('[WebAlias] Redirect:', this.getCheckoutUrl());

            window.location.assign(this.getCheckoutUrl());
        }

        async buyNowWithDiscount(element) {
            try {
                const productForm = element.closest?.('form[action*="/cart/add"]')
                    || document.querySelector('form[action*="/cart/add"]');

                if (!productForm) {
                    console.error('[WebAlias] Product form not found.');
                    return;
                }

                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {Accept: 'application/json'},
                    body: new FormData(productForm)
                });

                if (!response.ok) throw new Error(`Add to cart failed (${response.status})`);

                window.location.assign(this.getCheckoutUrl());
            } catch (error) {
                console.error('[WebAlias] Buy it now failed:', error);
            }
        }
    }
// GUEST --------------------------------------------------------------------------------------------------
    /**
     * Priority:
     * 1. Incoming ?als=
     * 2. Previously validated localStorage.WebAlias
     * 3. Corporate
     *
     * Guests may change sponsor by changing `als`.
     */
    class GuestWebAliasResolver extends BaseWebAliasResolver {
        async resolve({ incomingAlias, storedAlias, signal }) {
            if (incomingAlias) {
                const incomingResult = await this.validateCandidate(incomingAlias, signal, 'guest-incoming');
                return incomingResult || this.corporate('guest-invalid-incoming');
            }

            if (storedAlias) {
                const storedResult = await this.validateCandidate(storedAlias, signal, 'guest-stored');
                return storedResult || this.corporate('guest-invalid-stored');
            }

            return this.corporate('guest-no-alias');
        }
    }
// RETAIL --------------------------------------------------------------------------------
    /**
     * The backend determines whether the logged-in Retail customer already has
     * a lifetime/existing sponsor.
     *
     * If locked:
     *   existing sponsor ALWAYS wins over incoming `als` and localStorage.
     *
     * If not locked:
     *   incoming `als` -> stored alias -> Corporate.
     */
    class RetailWebAliasResolver extends BaseWebAliasResolver {
        async resolve({ customer, incomingAlias, storedAlias, signal }) {
            const customerSponsor = await this.api.resolveRetailCustomer(customer, signal);

            if (customerSponsor.locked) {
                if (!customerSponsor.alias) return this.corporate('retail-locked-sponsor-missing');

                const existingSponsor = await this.validateCandidate(customerSponsor.alias, signal, 'retail-existing-sponsor');
                if (existingSponsor) return { ...existingSponsor, locked: true };

                return this.corporate('retail-existing-sponsor-invalid');
            }

            if (incomingAlias) {
                const incomingResult = await this.validateCandidate(incomingAlias, signal, 'retail-incoming');
                return incomingResult || this.corporate('retail-invalid-incoming');
            }

            if (storedAlias) {
                const storedResult = await this.validateCandidate(storedAlias, signal, 'retail-stored');
                return storedResult || this.corporate('retail-invalid-stored');
            }

            return this.corporate('retail-no-sponsor');
        }
    }
// AFFILIATE ---------------------------------------------------------------------------------------------------------
    /**
     * Affiliate customers are always assigned to themselves.
     * Incoming `als` and localStorage are NOT sources of truth.
     */
    class AffiliateWebAliasResolver extends BaseWebAliasResolver {
        async resolve({ customer, signal }) {
            const affiliate = await this.api.resolveAffiliate(customer, signal);

            if (!affiliate.alias) return this.corporate('affiliate-self-missing');

            const ownAlias = await this.validateCandidate(affiliate.alias, signal, 'affiliate-self');
            if (ownAlias) return { ...ownAlias, locked: true };

            return this.corporate('affiliate-self-invalid');
        }
    }
// MANAGER
    class WebAliasManager {
        constructor(config) {
            this.config = this.validateConfig(config);
            this.storage = new WebAliasStorage();
            this.api = new WebAliasApi(this.config);

            this.resolvers = {
                [CUSTOMER_TYPE.GUEST]: new GuestWebAliasResolver(this.api, this.config.corporateAlias),
                [CUSTOMER_TYPE.RETAIL]: new RetailWebAliasResolver(this.api, this.config.corporateAlias),
                [CUSTOMER_TYPE.AFFILIATE]: new AffiliateWebAliasResolver(this.api, this.config.corporateAlias)
            };

            this.requestId = 0;
            this.abortController = null;
            this.lastResolvedAlias = null;
            this.handleUrlChange = this.handleUrlChange.bind(this);
        }

        validateConfig(config) {
            if (!config || typeof config !== 'object') throw new Error('Missing window.NuvitaWebAliasConfig.');

            const corporateAlias = normalizeAlias(config.corporateAlias);
            if (!corporateAlias) throw new Error('NuvitaWebAliasConfig.corporateAlias is required.');

            return {
                ...config,
                corporateAlias,
                apiBaseUrl: String(config.apiBaseUrl || ''),
                syncUrl: config.syncUrl !== false,
                debug: Boolean(config.debug),
                customer: {
                    loggedIn: Boolean(config.customer?.loggedIn),
                    email: config.customer?.email || null,
                    type: config.customer?.type || CUSTOMER_TYPE.GUEST
                }
            };
        }

        init() {
            this.installUrlListeners();
            return this.resolve();
        }

        destroy() {
            this.abortController?.abort();
            window.removeEventListener('popstate', this.handleUrlChange);
            window.removeEventListener('nuvita:urlchange', this.handleUrlChange);
        }

        getCustomerType() {
            return this.config.customer.type;
        }

        getIncomingAlias() {
            return normalizeAlias(new URL(window.location.href).searchParams.get(URL_PARAM));
        }

        async resolve() {
            const requestId = ++this.requestId;

            this.abortController?.abort();
            this.abortController = new AbortController();

            const customerType = this.getCustomerType();
            const resolver = this.resolvers[customerType];

            this.log('Resolving', {
                customerType,
                incomingAlias: this.getIncomingAlias(),
                storedAlias: this.storage.get()
            });

            try {
                const result = await resolver.resolve({
                    customer: this.config.customer,
                    incomingAlias: this.getIncomingAlias(),
                    storedAlias: this.storage.get(),
                    signal: this.abortController.signal
                });

                if (requestId !== this.requestId) return null;

                this.applyResult(result);
                return result;
            } catch (error) {
                if (error?.name === 'AbortError') return null;

                console.error('[WebAlias] Resolution failed:', error);
                return null;
            }
        }

        applyResult(result) {
            const alias = normalizeAlias(result?.alias);
            if (!alias) return;

            const previousAlias = this.storage.get();

            this.storage.set(alias, result.discountCode);
            this.lastResolvedAlias = alias;

            if (this.config.syncUrl) this.syncUrl(alias);

            this.log('Resolved', {
                alias,
                locked: Boolean(result.locked),
                source: result.source,
                previousAlias
            });

            if (previousAlias !== alias) {
                window.dispatchEvent(new CustomEvent('nuvita:web-alias:changed', {
                    detail: {
                        alias,
                        previousAlias,
                        locked: Boolean(result.locked),
                        source: result.source
                    }
                }));
            }
        }

        syncUrl(alias) {
            const canonicalAlias = normalizeAlias(alias);
            if (!canonicalAlias) return;

            const url = new URL(window.location.href);
            const currentAlias = normalizeAlias(url.searchParams.get(URL_PARAM));

            if (currentAlias === canonicalAlias) return;

            url.searchParams.set(URL_PARAM, canonicalAlias);
            history.replaceState(history.state, '', url.toString());
        }

        handleUrlChange() {
            const incomingAlias = this.getIncomingAlias();

            if (incomingAlias === this.lastResolvedAlias) return;

            this.log('URL alias changed', { incomingAlias });
            this.resolve();
        }

        installUrlListeners() {
            window.addEventListener('popstate', this.handleUrlChange);
            window.addEventListener('nuvita:urlchange', this.handleUrlChange);

            this.patchHistoryMethod('pushState');
            this.patchHistoryMethod('replaceState');
        }

        patchHistoryMethod(methodName) {
            const original = history[methodName];

            if (original.__nuvitaWebAliasPatched) return;

            const patched = function (...args) {
                const result = original.apply(this, args);
                window.dispatchEvent(new Event('nuvita:urlchange'));
                return result;
            };

            patched.__nuvitaWebAliasPatched = true;
            history[methodName] = patched;
        }

        log(message, data = null) {
            if (!this.config.debug) return;

            if (data) console.log(`[WebAlias] ${message}`, data);
            else console.log(`[WebAlias] ${message}`);
        }
    }

    // ADSGUN BYPASS (2026-10-19): skip all alias/discount logic for Adsgun visitors
    const isAdsgunVisitor = () => {
        try {
            let hit = false;
            new URLSearchParams(window.location.search).forEach((v, k) => {
                if (k.toLowerCase() === 'adsgunlink') hit = true;
            });
            if (hit) {
                try { sessionStorage.setItem('nv_adsgun', '1'); } catch (e) {}
                document.cookie = 'nv_adsgun=1; path=/; max-age=' + (7 * 24 * 60 * 60) + '; SameSite=Lax';
                return true;
            }
            try { if (sessionStorage.getItem('nv_adsgun') === '1') return true; } catch (e) {}
            return /(?:^|;\s*)nv_adsgun=1(?:;|$)/.test(document.cookie);
        } catch (e) {
            return false;
        }
    };

    const start = async () => {
        if (isAdsgunVisitor()) return;
        try {
            if (!window.NuvitaWebAliasConfig) {
                console.error('[WebAlias] window.NuvitaWebAliasConfig is not defined.');
                return;
            }

            const manager = new WebAliasManager(window.NuvitaWebAliasConfig);
            window.NuvitaWebAlias = manager;


            const checkout = new WebAliasCheckout(); // discount code 
            checkout.install(); //discount code 

            await manager.init();
        } catch (error) {
            console.error('[WebAlias] Initialization failed:', error);
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
        start();
    }
})();